import React, { useState, useEffect } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import styles from './Alert.module.css';

export type AlertType = 'success' | 'error' | 'warning' | 'info';
export type AlertPosition = 'top' | 'top-start' | 'top-end' | 'center' | 'bottom' | 'bottom-start' | 'bottom-end';

export interface AlertOptions {
  title?: string;
  text?: string;
  icon?: AlertType;
  showCancelButton?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
  position?: AlertPosition;
  timer?: number;
  allowOutsideClick?: boolean;
  allowEscapeKey?: boolean;
  showCloseButton?: boolean;
}

export interface AlertResult {
  isConfirmed: boolean;
  isDenied: boolean;
  isDismissed: boolean;
}

interface AlertProps extends AlertOptions {
  isOpen: boolean;
  onClose: (result: AlertResult) => void;
}

const Alert: React.FC<AlertProps> = ({
  isOpen,
  onClose,
  title = 'Alert',
  text = '',
  icon = 'info',
  showCancelButton = false,
  confirmButtonText = 'OK',
  cancelButtonText = 'Cancel',
  confirmButtonColor = '#3085d6',
  cancelButtonColor = '#d33',
  position = 'center',
  timer,
  allowOutsideClick = true,
  allowEscapeKey = true,
  showCloseButton = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsAnimating(true);
      
      // Auto close with timer
      if (timer && timer > 0) {
        const timeout = setTimeout(() => {
          handleClose({ isConfirmed: true, isDenied: false, isDismissed: false });
        }, timer);
        return () => clearTimeout(timeout);
      }
    } else {
      setIsAnimating(false);
      const timeout = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, timer]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (allowEscapeKey && event.key === 'Escape' && isOpen) {
        handleClose({ isConfirmed: false, isDenied: false, isDismissed: true });
      }
    };

    // Handle file input clicks - temporarily restore overflow
  const handleFileInputClick = (e: Event) => {
    const target = e.target as HTMLElement;
    
    if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'file') {
      // Temporarily restore overflow to allow file dialog
      document.body.style.overflow = "unset";
      // Restore hidden overflow after a short delay
      setTimeout(() => {
        if (isOpen) {
          document.body.style.overflow = "hidden";
        }
      }, 100);
    }
  };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.addEventListener('click', handleFileInputClick);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('click', handleFileInputClick);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, allowEscapeKey]);

  const handleClose = (result: AlertResult) => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose(result);
    }, 300);
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (allowOutsideClick && event.target === event.currentTarget) {
      handleClose({ isConfirmed: false, isDenied: false, isDismissed: true });
    }
  };

  const handleConfirm = () => {
    handleClose({ isConfirmed: true, isDenied: false, isDismissed: false });
  };

  const handleCancel = () => {
    handleClose({ isConfirmed: false, isDenied: true, isDismissed: false });
  };

  const getIconComponent = () => {
    const iconProps = { className: styles.alertIcon };
    
    switch (icon) {
      case 'success':
        return <CheckCircleIcon {...iconProps} />;
      case 'error':
        return <XCircleIcon {...iconProps} />;
      case 'warning':
        return <ExclamationTriangleIcon {...iconProps} />;
      case 'info':
      default:
        return <InformationCircleIcon {...iconProps} />;
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`${styles.alertOverlay} ${styles[`position-${position}`]} ${isAnimating ? styles.animating : ''}`}
      onClick={handleBackdropClick}
    >
      <div className={`${styles.alertContainer} ${styles[`icon-${icon}`]} ${isAnimating ? styles.alertAnimating : ''}`}>
        {showCloseButton && (
          <button 
            className={styles.closeButton}
            onClick={() => handleClose({ isConfirmed: false, isDenied: false, isDismissed: true })}
            aria-label="Close"
          >
            <XMarkIcon className={styles.closeIcon} />
          </button>
        )}
        
        <div className={styles.alertContent}>
          <div className={styles.alertIconContainer}>
            {getIconComponent()}
          </div>
          
          <div className={styles.alertText}>
            <h3 className={styles.alertTitle}>{title}</h3>
            {text && <p className={styles.alertMessage}>{text}</p>}
          </div>
        </div>
        
        <div className={styles.alertActions}>
          {showCancelButton && (
            <button
              className={`${styles.alertButton} ${styles.cancelButton}`}
              onClick={handleCancel}
              style={{ backgroundColor: cancelButtonColor }}
            >
              {cancelButtonText}
            </button>
          )}
          <button
            className={`${styles.alertButton} ${styles.confirmButton}`}
            onClick={handleConfirm}
            style={{ backgroundColor: confirmButtonColor }}
          >
            {confirmButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Alert;
